import { useEffect, useRef, useState } from "react";
import { BroadcastTransport } from "./BroadcastTransport";
import { WebSocketTransport } from "./WebSocketTransport";
import type { MpClient, MpEvent, MpPositionTick, MpRoomState } from "./types";

/**
 * Pick the transport based on environment:
 *   - localhost / 127.0.0.1 → BroadcastChannel (multi-tab dev testing)
 *   - everything else → WebSocket → Cloudflare RaceRoom Durable Object
 *
 * `?mp=ws` or `?mp=bc` in the URL forces a transport (handy for LAN testing).
 */
function pickTransport(): MpClient {
  if (typeof window !== "undefined") {
    const force = new URLSearchParams(window.location.search).get("mp");
    if (force === "ws") return new WebSocketTransport();
    if (force === "bc") return new BroadcastTransport();
    const host = window.location.hostname;
    const isLocal = host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
    return isLocal ? new BroadcastTransport() : new WebSocketTransport();
  }
  return new BroadcastTransport();
}
import type { AvatarConfig } from "@shared/types";

export interface RemotePosition extends MpPositionTick {
  id: string;
  updatedAt: number;
}

export interface UseMpRoomResult {
  client: MpClient | null;
  state: MpRoomState | null;
  positions: Map<string, RemotePosition>;
  error: string | null;
}

interface JoinOpts {
  code: string;
  asHost: boolean;
  me: { id: string; displayName: string; avatarConfig: AvatarConfig };
  lessonId?: string;
  seed?: number;
}

/**
 * React hook wrapping an MpClient. Re-renders on room state changes; tracks
 * remote position ticks in a Map for the game engine to consume.
 *
 * Auto-picks transport: BroadcastChannel for now (same-machine multi-tab).
 * When the Cloudflare backend is online we'll add a WebSocketTransport and
 * pick it for non-localhost hosts.
 */
export function useMpRoom(opts: JoinOpts | null): UseMpRoomResult {
  const [state, setState] = useState<MpRoomState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const positionsRef = useRef<Map<string, RemotePosition>>(new Map());
  const clientRef = useRef<MpClient | null>(null);
  // Bumping `tickRev` re-renders consumers without copying the map.
  const [, setTickRev] = useState(0);

  useEffect(() => {
    if (!opts) return;
    const client = pickTransport();
    clientRef.current = client;
    setError(null);
    let alive = true;
    let tickRaf = 0;
    let dirty = false;

    const off = client.on("event", (e: MpEvent) => {
      if (!alive) return;
      if (e.type === "state") {
        setState(e.state);
      } else if (e.type === "tick") {
        positionsRef.current.set(e.from, {
          id: e.from,
          ...e.tick,
          updatedAt: performance.now(),
        });
        dirty = true;
      } else if (e.type === "leave") {
        positionsRef.current.delete(e.from);
        dirty = true;
      } else if (e.type === "error") {
        setError(e.message);
      }
    });

    // Coalesce position updates into one render per animation frame so we
    // don't thrash React when 8 players are all sending ticks.
    const pump = () => {
      if (!alive) return;
      tickRaf = requestAnimationFrame(pump);
      if (dirty) {
        dirty = false;
        setTickRev((v) => v + 1);
      }
    };
    tickRaf = requestAnimationFrame(pump);

    client.join(opts).then(setState).catch((err) => setError(err.message));

    return () => {
      alive = false;
      cancelAnimationFrame(tickRaf);
      off();
      client.leave();
      clientRef.current = null;
    };
  }, [opts?.code, opts?.asHost, opts?.me.id, opts?.lessonId, opts?.seed]);

  return {
    client: clientRef.current,
    state,
    positions: positionsRef.current,
    error,
  };
}
